import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Calendar as CalendarIcon,
  GripVertical,
  Clock,
  ImagePlus,
  X
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function QuizBuilder() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  
  const [quizData, setQuizData] = useState({
    title: "",
    description: "",
    is_timed: false,
    time_limit_minutes: 30,
    due_date: null,
    questions: []
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  
  const getAuthImageUrl = (url) => {
    if (!url || !token) return url;
    // Normalize URL to current API base
    let normalizedUrl = url;
    if (url.includes('/api/')) {
      const apiPath = url.substring(url.indexOf('/api/') + 4);
      normalizedUrl = `${API}${apiPath}`;
    }
    const separator = normalizedUrl.includes('?') ? '&' : '?';
    return `${normalizedUrl}${separator}token=${token}`;
  };

  const addQuestion = () => {
    const newQuestion = {
      id: Date.now(),
      question_text: "",
      question_type: "multiple_choice",
      options: ["", "", "", ""],
      correct_answer: "",
      points: 1
    };
    setQuizData({
      ...quizData,
      questions: [...quizData.questions, newQuestion]
    });
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...quizData.questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuizData({ ...quizData, questions: updated });
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const updated = [...quizData.questions];
    updated[questionIndex].options[optionIndex] = value;
    setQuizData({ ...quizData, questions: updated });
  };

  const removeQuestion = (index) => {
    const updated = quizData.questions.filter((_, i) => i !== index);
    setQuizData({ ...quizData, questions: updated });
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;
    setUploadingImage(index);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload-file`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      const imageUrl = `${API}/files/${res.data.file_id}/download`;
      updateQuestion(index, "image_url", imageUrl);
      toast.success("Image uploaded!");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(null);
    }
  };

  const removeImage = (index) => {
    updateQuestion(index, "image_url", null);
  };

  const handleSubmit = async (mode = "draft", publishAt = null) => {
    if (!quizData.title) {
      toast.error("Please enter a quiz title");
      return;
    }
    if (quizData.questions.length === 0) {
      toast.error("Please add at least one question");
      return;
    }

    // Validate questions
    for (let i = 0; i < quizData.questions.length; i++) {
      const q = quizData.questions[i];
      if (!q.question_text) {
        toast.error(`Question ${i + 1} is empty`);
        return;
      }
      if (!q.correct_answer) {
        toast.error(`Question ${i + 1} needs a correct answer`);
        return;
      }
    }

    // Validate schedule
    if (mode === "scheduled") {
      if (!publishAt) {
        toast.error("Please pick a publish date/time");
        return;
      }
      const pa = new Date(publishAt);
      if (Number.isNaN(pa.getTime()) || pa <= new Date()) {
        toast.error("Publish time must be in the future");
        return;
      }
      if (quizData.due_date && new Date(quizData.due_date) <= pa) {
        toast.error("Due date must be after the publish time");
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        class_id: classId,
        title: quizData.title,
        description: quizData.description,
        is_timed: quizData.is_timed,
        time_limit_minutes: quizData.is_timed ? quizData.time_limit_minutes : null,
        due_date: quizData.due_date?.toISOString() || null,
        status: mode,
        publish_at: mode === "scheduled" ? new Date(publishAt).toISOString() : null,
        questions: quizData.questions.map(q => ({
          question_text: q.question_text,
          question_type: q.question_type,
          options: q.question_type === "multiple_choice" ? q.options.filter(o => o) : null,
          correct_answer: q.correct_answer,
          points: q.points,
          image_url: q.image_url || null
        }))
      };

      await axios.post(`${API}/quizzes`, payload, { headers });
      const msg = mode === "draft" ? "Quiz saved as draft" :
                  mode === "scheduled" ? "Quiz scheduled successfully" :
                  "Quiz published!";
      toast.success(msg);
      navigate(`/class/${classId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save quiz");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="quiz-builder">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/class/${classId}`)}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-lg font-bold text-foreground font-['Manrope']">Create Quiz</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => handleSubmit("draft")}
                className="rounded-full"
                disabled={saving}
                data-testid="save-draft-btn"
              >
                {saving ? "Saving..." : "Save as Draft"}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const d = new Date(Date.now() + 60 * 60 * 1000); // default +1 hour
                  d.setSeconds(0, 0);
                  setScheduleAt(d.toISOString().slice(0, 16));
                  setScheduleDialogOpen(true);
                }}
                className="rounded-full"
                disabled={saving}
                data-testid="schedule-btn"
              >
                <Clock className="w-4 h-4 mr-1" />
                Schedule
              </Button>
              <Button
                onClick={() => handleSubmit("published")}
                className="rounded-full bg-primary text-primary-foreground"
                disabled={saving}
                data-testid="publish-now-btn"
              >
                Publish Now
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Quiz Details */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="font-['Manrope']">Quiz Details</CardTitle>
            <CardDescription>Set the basic information for your quiz</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Quiz Title *</Label>
              <Input
                id="title"
                data-testid="quiz-title-input"
                placeholder="e.g., Chapter 1 Review"
                value={quizData.title}
                onChange={(e) => setQuizData({ ...quizData, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                data-testid="quiz-description-input"
                placeholder="Brief description of the quiz..."
                value={quizData.description}
                onChange={(e) => setQuizData({ ...quizData, description: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Due Date (Optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !quizData.due_date && "text-muted-foreground"
                      )}
                      data-testid="quiz-due-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {quizData.due_date ? format(quizData.due_date, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={quizData.due_date}
                      onSelect={(date) => setQuizData({ ...quizData, due_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="timed" className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Timed Quiz
                  </Label>
                  <Switch
                    id="timed"
                    data-testid="timed-switch"
                    checked={quizData.is_timed}
                    onCheckedChange={(checked) => setQuizData({ ...quizData, is_timed: checked })}
                  />
                </div>
                {quizData.is_timed && (
                  <div className="space-y-2">
                    <Label htmlFor="time-limit">Time Limit (minutes)</Label>
                    <Input
                      id="time-limit"
                      data-testid="time-limit-input"
                      type="number"
                      min={1}
                      value={quizData.time_limit_minutes}
                      onChange={(e) => setQuizData({ ...quizData, time_limit_minutes: parseInt(e.target.value) || 30 })}
                    />
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Questions */}
        <Card className="border-border/50">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-['Manrope']">Questions</CardTitle>
              <CardDescription>{quizData.questions.length} questions added</CardDescription>
            </div>
            <Button
              onClick={addQuestion}
              className="rounded-full bg-primary text-primary-foreground"
              data-testid="add-question-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Question
            </Button>
          </CardHeader>
          <CardContent>
            {quizData.questions.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-border rounded-xl">
                <p className="text-muted-foreground mb-4">No questions yet</p>
                <Button
                  onClick={addQuestion}
                  variant="outline"
                  className="rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Question
                </Button>
              </div>
            ) : (
              <AnimatePresence>
                <div className="space-y-4">
                  {quizData.questions.map((question, index) => (
                    <motion.div
                      key={question.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="p-4 border border-border/50 rounded-xl"
                      data-testid={`question-${index}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <GripVertical className="w-5 h-5 text-muted-foreground" />
                          <span className="text-sm font-medium text-muted-foreground">
                            Question {index + 1}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-2">
                            <Label className="text-sm">Points:</Label>
                            <Input
                              type="number"
                              min={1}
                              value={question.points}
                              onChange={(e) => updateQuestion(index, "points", parseInt(e.target.value) || 1)}
                              className="w-16 h-8"
                              data-testid={`question-${index}-points`}
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => removeQuestion(index)}
                            data-testid={`delete-question-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Question Type</Label>
                          <Select
                            value={question.question_type}
                            onValueChange={(value) => updateQuestion(index, "question_type", value)}
                          >
                            <SelectTrigger data-testid={`question-${index}-type`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                              <SelectItem value="true_false">True / False</SelectItem>
                              <SelectItem value="short_answer">Short Answer</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Question Text *</Label>
                          <Textarea
                            placeholder="Enter your question..."
                            value={question.question_text}
                            onChange={(e) => updateQuestion(index, "question_text", e.target.value)}
                            data-testid={`question-${index}-text`}
                          />
                        </div>

                        {/* Question Image */}
                        <div className="space-y-2">
                          {question.image_url ? (
                            <div className="relative inline-block">
                              <img
                                src={getAuthImageUrl(question.image_url)}
                                alt="Question"
                                className="max-h-48 rounded-lg border"
                              />
                              <Button
                                variant="destructive"
                                size="icon"
                                className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                                onClick={() => removeImage(index)}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <label
                              className="flex items-center gap-2 cursor-pointer text-sm text-muted-foreground hover:text-primary transition-colors"
                              data-testid={`question-${index}-add-image`}
                            >
                              <ImagePlus className="w-4 h-4" />
                              {uploadingImage === index ? "Uploading..." : "Add Image"}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(index, e.target.files[0])}
                                disabled={uploadingImage === index}
                              />
                            </label>
                          )}
                        </div>

                        {question.question_type === "multiple_choice" && (
                          <div className="space-y-2">
                            <Label>Options</Label>
                            <div className="space-y-2">
                              {question.options.map((option, optIndex) => (
                                <div key={optIndex} className="flex items-center gap-2">
                                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                                    {String.fromCharCode(65 + optIndex)}
                                  </span>
                                  <Input
                                    placeholder={`Option ${String.fromCharCode(65 + optIndex)}`}
                                    value={option}
                                    onChange={(e) => updateOption(index, optIndex, e.target.value)}
                                    data-testid={`question-${index}-option-${optIndex}`}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {question.question_type === "true_false" && (
                          <div className="space-y-2">
                            <Label>Options</Label>
                            <div className="flex gap-4">
                              <div className="flex items-center gap-2 p-3 border rounded-lg">
                                <span className="font-medium">True</span>
                              </div>
                              <div className="flex items-center gap-2 p-3 border rounded-lg">
                                <span className="font-medium">False</span>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label>Correct Answer *</Label>
                          {question.question_type === "multiple_choice" ? (
                            <Select
                              value={question.correct_answer}
                              onValueChange={(value) => updateQuestion(index, "correct_answer", value)}
                            >
                              <SelectTrigger data-testid={`question-${index}-correct`}>
                                <SelectValue placeholder="Select correct answer" />
                              </SelectTrigger>
                              <SelectContent>
                                {question.options.filter(o => o).map((option, i) => (
                                  <SelectItem key={i} value={option}>
                                    {String.fromCharCode(65 + i)}: {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : question.question_type === "true_false" ? (
                            <Select
                              value={question.correct_answer}
                              onValueChange={(value) => updateQuestion(index, "correct_answer", value)}
                            >
                              <SelectTrigger data-testid={`question-${index}-correct`}>
                                <SelectValue placeholder="Select correct answer" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="true">True</SelectItem>
                                <SelectItem value="false">False</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              placeholder="Enter the correct answer"
                              value={question.correct_answer}
                              onChange={(e) => updateQuestion(index, "correct_answer", e.target.value)}
                              data-testid={`question-${index}-correct`}
                            />
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </AnimatePresence>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Schedule Quiz Publish</DialogTitle>
            <DialogDescription>
              The quiz will stay hidden from students until this date/time, then auto-publish.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="schedule-at">Publish at</Label>
            <Input
              id="schedule-at"
              type="datetime-local"
              value={scheduleAt}
              min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
              onChange={(e) => setScheduleAt(e.target.value)}
              data-testid="schedule-datetime-input"
            />
            {quizData.due_date && (
              <p className="text-xs text-muted-foreground">
                Due date: <strong>{format(new Date(quizData.due_date), "PPP")}</strong> — publish time must be before this.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                setScheduleDialogOpen(false);
                handleSubmit("scheduled", scheduleAt);
              }}
              disabled={!scheduleAt || saving}
              className="bg-primary text-primary-foreground"
              data-testid="confirm-schedule-btn"
            >
              <Clock className="w-4 h-4 mr-1" />
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
